import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import useFlash from "go-alert/AlertMessage";
import { OrderContext } from "../contexts/OrderContext";
import { ActionRow } from "../services/types";
import { downloadEReceipt, downloadInvoice, handleEReceiptError } from "../utils";

export const useOrderActions = (
	orderNumber: string
): { actionsRows: ActionRow[]; isEmailModalOpen: boolean; hideEmailModal: () => void } => {
	const { t } = useTranslation();
	const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
	const [isDownloadEReceiptRequestOngoing, setIsDownloadEReceiptRequestOngoing] = useState(false);
	const [isDownloadInvoiceRequestOngoing, setIsDownloadInvoiceRequestOngoing] = useState(false);
	const { addFlash } = useFlash();
	const { orderId, order } = useContext(OrderContext);

	const actionsRows: ActionRow[] = [
		{
			actions: [
				{
					text: t("modules.e_order.action.download_e_receipt.title"),
					onClick: async () => {
						setIsDownloadEReceiptRequestOngoing(true);

						try {
							const filename = `${t("modules.e_order.field.filename.title")} #${orderNumber}.json`;

							await downloadEReceipt(filename, orderId);
						} catch (e) {
							handleEReceiptError(e, addFlash);
						}

						setIsDownloadEReceiptRequestOngoing(false);
					},
					isLoading: isDownloadEReceiptRequestOngoing,
					"data-testid": "e-order-order-actions-section-download-receipt-button",
					hide: !order?.e_receipt,
				},
				{
					text: t("modules.e_order.action.download_invoice.title"),
					hide: !order?.invoice_public_id,
					onClick: async () => {
						if (!order?.invoice_public_id) return;

						setIsDownloadInvoiceRequestOngoing(true);

						try {
							const filename = `${t("modules.e_order.field.filename.title")} #${orderNumber}.pdf`;

							await downloadInvoice(filename, order.invoice_public_id);
						} catch (e) {
							handleEReceiptError(e, addFlash);
						}

						setIsDownloadInvoiceRequestOngoing(false);
					},
					isLoading: isDownloadInvoiceRequestOngoing,
					"data-testid": "e-order-order-actions-section-download-invoice-button",
				},
				{
					text: t("modules.e_order.action.send_email.title"),
					onClick: () => setIsEmailModalOpen(true),
					"data-testid": "e-order-order-actions-section-send-email-button",
				},
			],
		},
	];

	return {
		actionsRows,
		isEmailModalOpen,
		hideEmailModal: () => setIsEmailModalOpen(false),
	};
};
